import { makeAutoObservable } from 'mobx';
import { inject, injectable } from 'inversify';
import { notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import { changeUserInfo } from '@shared/api/authRegistration';
import { HistoryModule } from '@mobx/services/history';
import { SubmitHandler } from 'react-hook-form';
import { UserInfo } from '@pages/auth/Register/config/validation';
import { reformatPhone } from '@shared/utils/helpers/formatString';
import { UserRoles } from '@mobx/interfaces';
import { clearPersistedStore, makePersistable } from 'mobx-persist-store';
import { NavigateFunction } from 'react-router-dom';
import { PATH_AUTH } from '@app/routes/paths';
import { IRegisterPageViewModel } from '@pages/auth/Register/types';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { queryClient } from '@shared/api/reactQuery';
import { getGetApiAuthGenerateOtpPhoneQueryOptions } from '@/shared/api/orval/auth/auth';

const apiUrl = import.meta.env.VITE_REACT_APP_API_URL;

const states = {
  confirmPhone: 'confirmPhone',
  verifyCode: 'verifyCode',
  changePassword: 'changePassword',
  userInfo: 'userInfo'
} as const;

export type WidgetStates = (typeof states)[keyof typeof states];

const DEFAULT_STATE: WidgetStates = 'confirmPhone';

const wsUrl = `${apiUrl?.replace('http', 'ws')}/ws`;

@injectable()
export class RegistrationWidgetViewModel implements IRegisterPageViewModel {
  state: WidgetStates = DEFAULT_STATE;

  socket: WebSocket;

  botUrl = '';

  phone = '';

  // Fix: флаг что пользователь уже делился контактом ранее (повторная регистрация)
  isContacted = false;

  userId: number | null = null;

  selectedVerifyMethod = 'Telegram' as const;

  navigate: NavigateFunction | null = null;

  constructor(
    private history: HistoryModule,
    @inject(AuthServiceIdentifier) private authModule: AuthService,
    @inject(RolesServiceIdentifier) private rolesModule: RolesService
  ) {
    makeAutoObservable(this);
    void this.loadPersist();

    this.socket = new WebSocket(wsUrl);

    this.socket.onmessage = (msg) => {
      if (msg.data === 'connection_timeout') {
        this.navigate?.(PATH_AUTH.checkContact);
        notifications.warning('Время ожидания истекло');
      }
      if (msg.data === 'success') {
        this.goToVerifyCode();
      }
    };

    this.socket.onopen = () => {
      this.startWaitingSocket();
    };
  }

  cleanup = () => {
    this.stopWaitingSocket();
    this.socket.close();
  };

  cancelRegistration = () => {
    this.cleanup();
    this.returnToLogin();
  };

  initFlow = async () => {
    if (this.isContacted) {
      await this.generateAndGoToVerifyCode();
    }
  };

  goToVerifyCode = () => {
    this.state = 'verifyCode';
    this.cleanup();
  };

  goToChangePassword = () => {
    this.state = 'changePassword';
  };

  goToUserInfo = () => {
    this.state = 'userInfo';
  };

  setUserId = (userId: number | null) => {
    this.userId = userId;
  };

  onSuccessVerifyCode = (userId: number | null) => {
    this.userId = userId;
    this.goToChangePassword();
  };

  returnToLogin = () => {
    this.history.navigateTo(PATH_AUTH.checkContact);
  };

  onPasswordChanged = () => {
    this.goToUserInfo();
  };

  onUserInfoSubmit: SubmitHandler<UserInfo> = async (values) => {
    try {
      if (!this.userId) return;

      const payload = {
        fio: values.fio,
        phone: reformatPhone(values.phone)
      };

      await changeUserInfo(this.userId, payload);

      await this.onRegistrationSuccess();
    } catch (e) {
      logger.error(e);
      notifications.error(e);
    }
  };

  // Fix: генерируем OTP через API (как в login flow) и переходим к вводу кода
  private generateAndGoToVerifyCode = async () => {
    try {
      const reformattedPhone = reformatPhone(this.phone);
      const options = getGetApiAuthGenerateOtpPhoneQueryOptions(reformattedPhone, {
        query: { gcTime: 0, staleTime: 0 }
      });
      await queryClient.fetchQuery(options);
      this.goToVerifyCode();
    } catch (e) {
      logger.error(e);
      notifications.error(e);
    }
  };

  private loadPersist = async () => {
    if (window.history.state?.usr?.isFreshRegistration) {
      await clearPersistedStore(this);
      return;
    }

    await makePersistable(this, {
      name: 'RegistrationWidgetViewModel',
      properties: ['state', 'botUrl', 'userId']
    });

    console.debug('currentState', this.state);
  };

  private startWaitingSocket = () => {
    const rawPhone = reformatPhone(this.phone);
    this.sendMessage({
      name: 'wait_end_registration',
      data: {
        phone: rawPhone
      }
    });
  };

  private stopWaitingSocket = () => {
    this.sendMessage({
      name: 'stop_wait_end_registration'
    });
  };

  private onRegistrationSuccess = async () => {
    if (!this.userId) return;

    await this.authModule.authUserAfterRegistration(this.userId);
    const role = this.authModule.userData?.roles?.find((role) => role.role === UserRoles.ABONENT);
    if (!role) return;

    this.rolesModule.selectRole(role);
  };

  private sendMessage = (message: { name?: string; data?: any }) => {
    if (!this.socket?.readyState) return;

    if (this.socket?.readyState === WebSocket.CLOSED) return;

    this.socket.send(JSON.stringify(message));
  };
}
